import apiClient from '../clients/apiClient';
import { Schemas } from '../types/api-types';

const API_BASE = '/standing-order';

export interface StandingOrderRecord {
    id: number;
    branch_id: number | null;
    from_account_id: number | null;
    to_account_id: number | null;
    amount: number | null;
    process_date: string | null;
    status: number | null;
    c_at: string | null;
    c_by: number | null;
    m_at: string | null;
    m_by: number | null;
    from_account_number: string | null;
    from_account_name: string | null;
    to_account_number: string | null;
    to_account_name: string | null;
    from_customer_number: string | null;
    from_customer_name: string | null;
    to_customer_number: string | null;
    to_customer_name: string | null;
}

export interface StandingOrderPreview {
    process_date: string;
    from_account_id: number;
    to_account_id: number;
    amount: number;
    from_account_number: string | null;
    from_account_name: string | null;
    to_account_number: string | null;
    to_account_name: string | null;
}

export interface CreateStandingOrderRequest {
    from_account_id: number;
    to_customer_number: string;
    to_account_id: number;
    from_date: string;
    period: number;
    amount: number;
}

export interface CreateStandingOrderResponse {
    message: string;
    records_created: number;
    records: StandingOrderRecord[];
}

export interface StandingOrderQueueRecord {
    id: number;
    process_date: string | null;
    amount: number | null;
    from_account_id: number | null;
    to_account_id: number | null;
    from_account_number: string | null;
    to_account_number: string | null;
    from_customer_number: string | null;
    from_customer_name: string | null;
    to_customer_number: string | null;
    to_customer_name: string | null;
    from_account_balance: number | null;
    to_account_balance: number | null;
    from_gl_account_id: number | null;
    to_gl_account_id: number | null;
}

export interface StandingOrderProcessRequest {
    order_ids: number[];
    initial_balances: Record<number, number>;
}

export interface StandingOrderProcessResponse {
    processed_count: number;
    processed_ids: number[];
}

export const getStandingOrdersByAccount = async (accountId: number): Promise<StandingOrderRecord[]> => {
    const response = await apiClient.get(`${API_BASE}/account/${accountId}`);
    return response.data.data;
};

export const previewStandingOrder = async (request: CreateStandingOrderRequest): Promise<StandingOrderPreview[]> => {
    const response = await apiClient.post(`${API_BASE}/preview`, request);
    return response.data.data;
};

export const createStandingOrder = async (request: CreateStandingOrderRequest): Promise<CreateStandingOrderResponse> => {
    const response = await apiClient.post(`${API_BASE}/create`, request);
    return response.data.data;
};

export interface BulkCreateStandingOrdersRequest {
    records: Array<{
        from_account_id: number;
        to_account_id: number;
        amount: number;
        process_date: string;
    }>;
}

export const bulkCreateStandingOrders = async (request: BulkCreateStandingOrdersRequest): Promise<CreateStandingOrderResponse> => {
    const response = await apiClient.post(`${API_BASE}/bulk-create`, request);
    return response.data.data;
};

export const getStandingOrderCustomerSavingAccounts = async (customerId: number): Promise<Schemas['PlAccountResponse'][]> => {
    const response = await apiClient.get(`${API_BASE}/customer/${customerId}/saving-accounts`);
    return response.data.data;
};

export const deleteStandingOrder = async (orderId: number): Promise<boolean> => {
    const response = await apiClient.delete(`${API_BASE}/${orderId}`);
    return response.data.data;
};

export const updateStandingOrderStatus = async (orderId: number, status: number): Promise<StandingOrderRecord> => {
    const response = await apiClient.patch(`${API_BASE}/${orderId}/status`, null, {
        params: { status }
    });
    return response.data.data;
};

export const getStandingOrderQueue = async (): Promise<StandingOrderQueueRecord[]> => {
    const response = await apiClient.get(`${API_BASE}/queue`);
    return response.data.data;
};

export interface StandingOrderProcessRequest {
    order_ids: number[];
    initial_balances: Record<number, number>;
}

export interface StandingOrderProcessResponse {
    processed_count: number;
    processed_ids: number[];
}

export const processStandingOrders = async (
    request: StandingOrderProcessRequest
): Promise<StandingOrderProcessResponse> => {
    const response = await apiClient.post(`${API_BASE}/process`, request);
    return response.data.data;
};

export interface BulkImportValidateRow {
    row_num: number;
    from_account_number: string;
    to_account_number: string;
    customer_number?: string | null;
    start_date: string;
    period: number;
    amount: number;
    from_account_id?: number | null;
    to_account_id?: number | null;
    from_account_name?: string | null;
    to_account_name?: string | null;
}

export interface BulkImportValidationError {
    row: number;
    message: string;
}

export interface BulkImportValidationResponse {
    is_valid: boolean;
    total_rows: number;
    skipped_empty_rows: number;
    valid_rows: BulkImportValidateRow[];
    errors: BulkImportValidationError[];
}

export interface BulkImportProcessResponse {
    success: boolean;
    message: string;
    records_created: number;
    rows_processed: number;
}

export const downloadStandingOrderBulkTemplate = async (fromProductId: number, toProductId: number): Promise<void> => {
    const response = await apiClient.get(`${API_BASE}/bulk-import/template`, {
        params: {
            from_product_id: fromProductId,
            to_product_id: toProductId
        },
        responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'standing_order_bulk_template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const validateStandingOrderBulkFile = async (file: File): Promise<BulkImportValidationResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${API_BASE}/bulk-import/validate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data;
};

export const processStandingOrderBulkImport = async (rows: BulkImportValidateRow[]): Promise<BulkImportProcessResponse> => {
    const response = await apiClient.post(`${API_BASE}/bulk-import/process`, { rows });
    return response.data.data;
};
